import { useState } from "react";
import {
  BID_MAX_WORDS,
  BID_MIN_WORDS,
  BIDS_REQUIRED,
  COMPETITIVE_MIN,
  LEARNING_BIDS,
  type Bid,
  type BidScreen as BidScreenData,
} from "../../api/pitch";
import { AIFeedbackCard } from "../../components/AIFeedbackCard";
import { BackButton } from "../../components/BackButton";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Checks } from "../../components/Checks";
import { Field, inputShell } from "../../components/Field";
import { Guide } from "../../components/Guide";
import { ScreenHeader } from "../../components/ScreenHeader";
import { wordCount } from "../../lib/format";

interface BidScreenProps {
  screen: BidScreenData;
  result: Bid | null;
  bidsCounted: number;
  busy: boolean;
  error: string | null;
  onSubmit: (price: string, text: string) => void;
  onToggleShortlist: () => void;
  onNext: () => void;
}

/** One job: the brief, a model proposal on learning bids, and the participant's proposal with server-side scoring. */
export function BidScreen({
  screen,
  result,
  bidsCounted,
  busy,
  error,
  onSubmit,
  onToggleShortlist,
  onNext,
}: BidScreenProps) {
  const { job, sample } = screen;
  const [price, setPrice] = useState(screen.bid ? String(screen.bid.price) : "");
  const [text, setText] = useState(screen.bid?.text ?? "");
  const words = wordCount(text);
  const wordsBad = words < BID_MIN_WORDS || words > BID_MAX_WORDS;
  const shown = result ?? screen.bid ?? null;

  return (
    <div>
      <ScreenHeader
        kicker={`Step 3 · Pitch · ${screen.learning ? "Learning bid" : "Competitive bid"}`}
        title={job.title}
        sub={`${job.client} · ${job.loc} · $${job.lo}–$${job.hi} · ${job.posted} · ${job.proposals} proposals`}
      />
      <Guide guideKey="bid" done={Boolean(screen.bid?.counts)} />
      <Banner tone="note">
        <b>The brief:</b> {job.desc}
      </Banner>

      {sample ? (
        <div className="my-4 rounded-[var(--radius-card)] border border-border bg-surface-high p-4">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.14em] text-kicker">
            Model proposal {screen.learningIndex + 1} of {LEARNING_BIDS}: learn from it, don't copy
            it
          </div>
          <div className="mb-2 text-[13px] text-text-muted">
            Written for a different job: <b>{sample.jobTitle}</b> for {sample.jobClient}, budget $
            {sample.lo} to ${sample.hi}.
          </div>
          <div className="whitespace-pre-wrap rounded-[9px] bg-surface p-3 text-[13.5px]">
            {sample.text}
          </div>
          <ul className="m-0 mt-3 list-none p-0 text-[13px]">
            {sample.checks.map((check) => (
              <li key={check.label} className="mb-1">
                <b className="text-positive">✓</b> <b>{check.label}.</b> {check.fail}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[12.5px] text-text-muted">
            No paid clients yet? Your proof can be a portfolio piece or a practice project. Never
            invent results or clients.
          </p>
        </div>
      ) : null}

      <Banner tone="note">
        {screen.learning ? (
          <>
            <b>Learning bid.</b> It counts once it's at least {BID_MIN_WORDS} words and written in
            your own words. Follow the structure of the model: name, need, proof, plan, price,
            question.
          </>
        ) : (
          <>
            <b>Competitive bid.</b> It counts only if it scores at least {COMPETITIVE_MIN} of 7,
            including personalized, addresses the need and leads with proof.
          </>
        )}
      </Banner>

      <Field label="Your price (USD)" htmlFor="bid-price">
        <input
          id="bid-price"
          inputMode="numeric"
          className={inputShell}
          placeholder={`e.g. ${Math.round((job.lo + job.hi) / 2)}`}
          value={price}
          onChange={(event) => setPrice(event.target.value)}
        />
      </Field>
      <Field label="Your proposal" htmlFor="bid-text">
        <textarea
          id="bid-text"
          rows={10}
          className={inputShell}
          placeholder={`Hi ${job.client}, ...`}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <span
          className={`text-[12.5px] ${wordsBad ? "text-negative" : "text-text-muted"}`}
          aria-live="polite"
        >
          {words} words
          {words < BID_MIN_WORDS
            ? ` (minimum ${BID_MIN_WORDS})`
            : words > BID_MAX_WORDS
              ? ` (maximum ${BID_MAX_WORDS})`
              : ""}
        </span>
      </Field>
      {error ? (
        <p role="alert" className="text-[13.5px] text-negative">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2.5">
        <Button
          disabled={busy || !text.trim() || !price.trim()}
          onClick={() => onSubmit(price.trim(), text.trim())}
        >
          {busy ? "Scoring..." : "Submit bid"}
        </Button>
        <Button variant="ghost" aria-pressed={screen.shortlisted} onClick={onToggleShortlist}>
          {screen.shortlisted ? "★ Shortlisted" : "☆ Shortlist"}
        </Button>
        <BackButton to="/gig/3" label="Back to job board" />
      </div>

      {shown ? (
        <div className="mt-5" aria-live="polite">
          {shown.counts ? (
            <Banner tone="good">
              ✓ Counted. Application-readiness score {shown.pct}%. {bidsCounted} of {BIDS_REQUIRED}{" "}
              bids counted.
            </Banner>
          ) : (
            <Banner tone="bad">
              <b>Not counted yet</b> (score {shown.pct}%). Fix this and resubmit:
              <ul className="m-0 mt-1.5 pl-5">
                {shown.blocks.map((block) => (
                  <li key={block}>{block}</li>
                ))}
              </ul>
            </Banner>
          )}
          <Checks checks={shown.checks} />
          {shown.aiFeedback ? (
            <AIFeedbackCard feedback={shown.aiFeedback} title="How a client might read it" />
          ) : null}
          {shown.counts ? (
            <div className="mt-3">
              <Button onClick={onNext}>Find the next job →</Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
